import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail,
  Send,
  Loader2,
  ShieldCheck,
  ArrowLeft,
  CheckCircle2,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { submitContact } from "../lib/api";

export default function ContactPage() {
  const [form, setForm] = useState({ nom: "", email: "", sujet: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message envoyé", {
        description: "Olivier reviendra vers toi dès que possible.",
      });
      setForm({ nom: "", email: "", sujet: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Impossible d'envoyer le message. Réessaye dans quelques minutes.";
      toast.error("Envoi impossible", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="contact-page"
      className="mx-auto max-w-3xl px-4 py-14 sm:px-8"
    >
      <Link
        to="/"
        data-testid="contact-back-home"
        className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-cyan-400 hover:text-cyan-300"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour à l'accueil
      </Link>

      <div className="mb-10">
        <span className="font-mono text-xs uppercase tracking-widest text-cyan-400">
          Contact direct
        </span>
        <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
          Une question, une suggestion ?
        </h1>
        <p className="mt-3 max-w-xl text-sm text-slate-400 sm:text-base">
          Olivier, fondateur d'Autopièce Malin, répond personnellement à chaque
          message. Ton message arrive directement dans sa boîte mail — sans
          intermédiaire, sans spam, sans liste de diffusion.
        </p>
      </div>

      <div className="surface-card rounded-2xl p-6 sm:p-8">
        {sent && (
          <div
            data-testid="contact-success"
            className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4"
          >
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-200">Message bien reçu !</p>
              <p className="mt-1 text-emerald-100/80">
                Olivier a reçu ton message et te répondra à l'adresse e-mail
                indiquée.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid gap-4" data-testid="contact-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="contact-nom" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-slate-400">
                Ton nom
              </Label>
              <Input
                id="contact-nom"
                data-testid="contact-field-nom"
                value={form.nom}
                onChange={update("nom")}
                required
                minLength={2}
                placeholder="Jean Dupont"
                className="border-slate-700 bg-slate-900/60 text-slate-100 placeholder:text-slate-500 focus-visible:ring-cyan-500"
              />
            </div>
            <div>
              <Label htmlFor="contact-email" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-slate-400">
                Ton e-mail
              </Label>
              <Input
                id="contact-email"
                data-testid="contact-field-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                placeholder="jean@exemple.fr"
                className="border-slate-700 bg-slate-900/60 text-slate-100 placeholder:text-slate-500 focus-visible:ring-cyan-500"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="contact-sujet" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-slate-400">
              Sujet
            </Label>
            <Input
              id="contact-sujet"
              data-testid="contact-field-sujet"
              value={form.sujet}
              onChange={update("sujet")}
              required
              minLength={3}
              placeholder="Suggestion de fournisseur / bug / question..."
              className="border-slate-700 bg-slate-900/60 text-slate-100 placeholder:text-slate-500 focus-visible:ring-cyan-500"
            />
          </div>

          <div>
            <Label htmlFor="contact-message" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-slate-400">
              Message
            </Label>
            <Textarea
              id="contact-message"
              data-testid="contact-field-message"
              value={form.message}
              onChange={update("message")}
              required
              minLength={10}
              maxLength={5000}
              rows={7}
              placeholder="Dis-nous tout : une pièce manquante, un fournisseur à ajouter, une amélioration..."
              className="resize-none border-slate-700 bg-slate-900/60 text-slate-100 placeholder:text-slate-500 focus-visible:ring-cyan-500"
            />
            <p className="mt-1 text-right font-mono text-[10px] uppercase tracking-widest text-slate-600">
              {form.message.length} / 5000
            </p>
          </div>

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-cyan-400" />
              Tes données ne sont pas revendues (
              <Link
                to="/politique-confidentialite"
                className="text-cyan-400 hover:text-cyan-300"
              >
                politique
              </Link>
              )
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="btn-primary-glow gap-2 bg-cyan-500 px-6 font-semibold text-slate-950 hover:bg-cyan-400"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Envoyer le message
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
        <Mail className="h-3.5 w-3.5" />
        Ton message est transmis directement à l'éditeur du site, sans partage à un tiers.
      </div>
    </div>
  );
}
